import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { mergePdfs } from '../services/pdfService';
import { DownloadIcon, MergeIcon, SpinnerIcon, ErrorIcon } from './icons';
import { useTranslation } from '../i18n';

const MergeTool: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleFilesSelect = useCallback((selectedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setError(null);
    setResultUrl(null);
  }, []);

  const handleRemoveFile = useCallback((fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  }, []);

  const handleMerge = async () => {
    if (files.length < 2) {
      setError(t('errorMergeCount'));
      return;
    }
    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setResultUrl(null);
    try {
      const mergedBlob = await mergePdfs(files, setProgress);
      setIsProcessing(true);
      const url = URL.createObjectURL(mergedBlob);
      setResultUrl(url);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <FileUpload onFilesSelect={handleFilesSelect} multiple={true} accept=".pdf" />
      
      {files.length > 0 && <FileList files={files} onRemoveFile={handleRemoveFile} />}
      
      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}

      <div className="mt-6">
        <button
          onClick={handleMerge}
          disabled={isLoading || files.length < 2}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <MergeIcon />}
          <span>{isLoading ? (isProcessing ? t('statusProcessing') : t('statusUploading')) : t('mergeButton')}</span>
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {resultUrl && (
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">{t('successTitle')}</h3>
          <p className="text-sm mb-4">{t('successMergeMessage')}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={resultUrl}
              download="merged.pdf"
              className="flex w-full items-center justify-center gap-3 bg-green-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 transition-all duration-200"
            >
              <DownloadIcon/>
              {t('downloadMergedButton')}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default MergeTool;
